import { useEffect, useState } from 'react'
import { useCardAccount } from '@/src/experiences/card-accounts/hooks/useCardAccount'
import { useAlert } from '@/src/components/ui/elements/dialogs/useAlert'
import { routerBack } from '@/src/common/utils/routerBack'
import { Linking } from 'react-native'
import { useLogError } from '@/src/common/hooks/useLogError'
import { VirtualCardApi } from '@/src/experiences/virtual-card/store'
import type { VirtualCardComponent } from '@/src/experiences/virtual-card/withVirtualCard'
import { WebCompatibleWebView } from '@/src/common/components/WebCompatibleWebView'
import { z } from 'zod/v4'
import Toast from 'react-native-toast-message'
import { vibrateError, vibrateSuccess } from '@/src/common/utils/vibrate'
import { cardBackStyles } from '@/src/app/main/(tabs)/card/card-management/virtual-card/CardBack'
import * as Sentry from '@sentry/react-native'
import { isProduction } from '@/src/common/environment'
import { useCombinedCardStatusStore } from '@/src/experiences/virtual-card/store/useCombinedCardStatusStore'

/** appears way too small on ios without this for some reason */
const fixMobileViewportScale = `
  document.head.insertAdjacentHTML('afterbegin', '<meta name="viewport" content="width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no">')
`

/**
 * need to extend lithic's source to not use `targetOrigin` (in mobile environments, there are no origins)
 * https://docs.lithic.com/docs/embedded-card-ui#response
 */
const lithicEmbedMobileExtension = `
  const originalCopySuccess = copySuccess;
  copySuccess = (...args) => {
    originalCopySuccess(...args)
    const copyElt = args[0];
    window.ReactNativeWebView.postMessage(JSON.stringify({copyElt, isCopied: true}));
  }

  const originalCopyFailed = copyFailed;
  copyFailed = (...args) => {
    originalCopyFailed(...args)
    const copyElt = args[0];
    window.ReactNativeWebView.postMessage(JSON.stringify({copyElt, isCopied: false}));
  }
`

/** https://github.com/react-native-webview/react-native-webview/blob/master/docs/Guide.md#the-injectedjavascript-prop */
const avoidSilentRNWebViewFailures = `true;`

const injectedJavascript = `
  ${fixMobileViewportScale}

  ${lithicEmbedMobileExtension}

  ${avoidSilentRNWebViewFailures}
` as const

export function PlatformVirtualCardAccount({ ToWrap }: { ToWrap: VirtualCardComponent }) {
  const Alert = useAlert(PlatformVirtualCardAccount.name)
  const logError = useLogError()
  const query = useCardAccount()
  const firstCardAccount = query.data?.[0]

  useEffect(() => {
    if (!query.isUninitialized && !query.isLoading && !query.isFetching && !firstCardAccount) {
      logError(
        `Card account summary attempted to be accessed when no card application was found`,
        query,
      )
      Alert.alert(
        `Card account not found`,
        'Please try again later or contact support@mesamember.com',
        [
          { text: 'OK', onPress: () => void routerBack() },
          {
            text: 'Contact support',
            onPress: () => void Linking.openURL('mailto:support@mesamember.com'),
          },
        ],
        {
          cancelable: false,
          closeableByOverlay: false,
          onDismiss: () => void routerBack(),
        },
      )
    }
  }, [query, firstCardAccount, Alert, logError])

  if (!firstCardAccount) {
    return <ToWrap />
  }

  return <PlatformVirtualCard ToWrap={ToWrap} cardAccountId={firstCardAccount.id} />
}

/** @see {@link https://docs.lithic.com/docs/embedded-card-ui#response} */
const LithicEmbedMessage = z.object({ copyElt: z.string(), isCopied: z.boolean() })

/** @todo this will eventually need to process different card platforms, e.g. lithic, etc. (right now, we just assume lithic is the only card platform) */
function PlatformVirtualCard({
  ToWrap,
  cardAccountId,
}: {
  ToWrap: VirtualCardComponent
} & (typeof VirtualCardApi.endpoints)['GET /card/:cardAccountId/virtual']['Types']['QueryArg']['params']) {
  const Alert = useAlert(PlatformVirtualCard.name)
  const logError = useLogError()
  const query = VirtualCardApi.endpoints['GET /card/:cardAccountId/virtual'].useQuery({
    params: { cardAccountId },
  })
  const [embedLoaded, setEmbedLoaded] = useState(false)
  const paymentMethodsQuery = VirtualCardApi.endpoints[
    'GET /card/:cardAccountId/payments/methods'
  ].useQuery({ params: { cardAccountId } })
  const { setStatus } = useCombinedCardStatusStore()
  const shipmentQuery = VirtualCardApi.endpoints['GET /card/:cardAccountId/shipment'].useQuery({
    params: { cardAccountId },
  })

  useEffect(() => {
    if (!query.isUninitialized && !query.isLoading && !query.isFetching && !query.data?.url) {
      logError(`url unavailable for displaying virtual card`, query)
      Alert.alert(
        `Card account not found`,
        'Please try again later or contact support@mesamember.com',
      )
    }

    if (query.data?.status) {
      setStatus(query.data.status)
    } else if (!query.isUninitialized && !query.isLoading && query.error) {
      setStatus(query)
    }
  }, [query, Alert, logError, setStatus])

  if (!query.data) {
    return <ToWrap freezeParams={{ cardAccountId }} />
  }

  return (
    <ToWrap
      freezeParams={{ cardAccountId }}
      cardLoaded={embedLoaded}
      /** @todo */
      cardAccountActive
      paymentMethodsLoaded={!paymentMethodsQuery.isUninitialized && !paymentMethodsQuery.isLoading}
      hasPaymentProfile={!!paymentMethodsQuery.data?.profiles.length}
      shipmentStatus={shipmentQuery.data?.status || undefined}
      cardBackContent={
        <Sentry.Mask style={cardBackStyles.cardContent}>
          <WebCompatibleWebView
            webviewDebuggingEnabled={!isProduction}
            style={{ backgroundColor: 'transparent' }}
            source={query.data.url}
            iframeProps={{ allow: 'clipboard-write' }}
            injectedJavaScript={injectedJavascript}
            onLoad={() => setEmbedLoaded(true)}
            onError={(e) => {
              logError(`error loading virtual card embed`, e)
              Alert.alert(
                `Virtual card could not be loaded`,
                'Please try again later or contact support@mesamember.com',
              )
            }}
            onMessage={(e) => {
              // todo: use https://zod.dev/codecs?id=jsonschema once we fully upgrade to zod 4 in the `lib` package

              const data = LithicEmbedMessage.parse(
                'nativeEvent' in e ? JSON.parse(z.string().parse(e.nativeEvent.data)) : e.data,
              )

              const name = formatCopyElt(data.copyElt)

              if (data.isCopied) {
                Toast.hide()
                Toast.show({
                  text1: `${name} copied`,
                  type: 'success',
                  position: 'bottom',
                  bottomOffset: 88,
                  onShow: () => void vibrateSuccess(),
                })
              } else {
                Toast.hide()
                Toast.show({
                  text1: `Failed to copy ${name}`,
                  type: 'error',
                  position: 'bottom',
                  bottomOffset: 88,
                  onShow: () => void vibrateError(),
                })
              }
            }}
          />
        </Sentry.Mask>
      }
    />
  )
}

function formatCopyElt(copyElt: z.infer<typeof LithicEmbedMessage>['copyElt']) {
  switch (copyElt) {
    case 'month':
      return 'Expiration Month'
    case 'year':
      return 'Expiration Year'
    case 'pan':
      return 'Card Number'
    default:
      return copyElt.toUpperCase()
  }
}
